import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { MoonMark as Moon } from "@/components/MoonMark";
import { toast } from "sonner";
import { api, mediaUrl, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import MoonDivider from "@/components/MoonDivider";
import CheckoutAuthDialog from "@/components/CheckoutAuthDialog";

const getCustomer = () => {
  try { return JSON.parse(localStorage.getItem("adc_customer")); } catch { return null; }
};

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();
  const [shippingRates, setShippingRates] = useState([]);
  const [threshold, setThreshold] = useState(75);
  const [shipping, setShipping] = useState("standard");
  const [loading, setLoading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const rates = r.data.shipping_rates || [];
      setShippingRates(rates);
      setThreshold(r.data.free_shipping_threshold ?? 75);
      if (rates.length && !rates.find((s) => s.id === shipping)) setShipping(rates[0].id);
    }).catch(() => {});
  }, []);

  const qualifies = subtotal >= threshold;
  const isFree = (id) => qualifies && id === "standard";
  const shipCost = isFree(shipping) ? 0 : (shippingRates.find((s) => s.id === shipping)?.price || 0);
  const total = subtotal + shipCost;
  const remaining = Math.max(0, threshold - subtotal);

  const startCheckout = () => {
    const customer = getCustomer();
    if (customer?.user) {
      proceed(customer.user.email, true);
    } else {
      setAuthOpen(true);
    }
  };

  const proceed = async (customerEmail, marketingOptIn = true) => {
    setAuthOpen(false);
    setLoading(true);
    try {
      const res = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_sku: i.variant_sku })),
        shipping_option: shipping,
        origin_url: window.location.origin,
        customer_email: customerEmail || undefined,
        marketing_opt_in: marketingOptIn,
      });
      if (window.fbq) window.fbq("track", "InitiateCheckout", { value: total, currency: "USD" });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="empty-cart-page">
        <Moon className="w-10 h-10 text-gold mx-auto mb-6" strokeWidth={1.2} />
        <h1 className="font-serif text-4xl text-cream mb-4">Your bag is empty</h1>
        <p className="font-body text-white/50 mb-8">Temptation awaits in the boutique.</p>
        <Link to="/shop" className="px-8 py-4 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors">Explore the Boutique</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="cart-page">
      <div className="text-center mb-4">
        <h1 className="font-serif text-5xl sm:text-6xl text-cream">Your Bag</h1>
      </div>
      <MoonDivider />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-12">
        <div className="lg:col-span-2 space-y-6">
          {items.map((i) => (
            <div key={i.key} className="flex gap-5 border-b border-white/10 pb-6" data-testid={`cart-row-${i.key}`}>
              <div className="w-24 h-28 bg-noir-2 overflow-hidden shrink-0">
                {i.image && <img src={mediaUrl(i.image)} alt={i.name} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1">
                <div className="flex justify-between gap-3">
                  <Link to={`/product/${i.product_id}`} className="font-serif text-xl text-cream hover:text-gold transition-colors">{i.name}</Link>
                  <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.key}`}><X className="w-4 h-4 text-white/40 hover:text-gold" /></button>
                </div>
                {i.variant_label && <p className="font-body text-xs uppercase tracking-[0.15em] text-champagne/80 mt-1" data-testid={`cart-variant-${i.key}`}>{i.variant_label}</p>}
                <p className="font-body text-gold mt-1">{money(i.price)}</p>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center border border-white/15">
                    <button onClick={() => updateQty(i.key, i.quantity - 1)} className="px-3 py-2 hover:text-gold" data-testid={`cart-dec-${i.key}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="font-body w-8 text-center text-sm">{i.quantity}</span>
                    <button onClick={() => updateQty(i.key, i.quantity + 1)} className="px-3 py-2 hover:text-gold" data-testid={`cart-inc-${i.key}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="font-body text-cream">{money(i.price * i.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
          <button onClick={clear} className="font-body text-xs uppercase tracking-widest text-white/40 hover:text-gold" data-testid="clear-cart">Empty bag</button>
        </div>

        <div className="bg-noir-2 border border-white/10 p-7 h-fit">
          <h2 className="font-serif text-2xl text-cream mb-6">Order Summary</h2>

          <div className="space-y-4 mb-6">
            <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne">Shipping</p>
            {shippingRates.map((s) => (
              <label key={s.id} className={`flex items-center justify-between gap-3 border px-4 py-3 cursor-pointer transition-colors ${shipping === s.id ? "border-gold" : "border-white/10 hover:border-white/25"}`} data-testid={`shipping-${s.id}`}>
                <div className="flex items-center gap-3">
                  <input type="radio" name="shipping" checked={shipping === s.id} onChange={() => setShipping(s.id)} className="accent-[#D4AF37]" />
                  <div>
                    <p className="font-body text-sm text-cream">{s.label}</p>
                    <p className="font-body text-xs text-white/40">{s.detail}</p>
                  </div>
                </div>
                <span className="font-body text-sm text-gold">{isFree(s.id) ? "Free" : money(s.price)}</span>
              </label>
            ))}
          </div>

          {remaining > 0 ? (
            <p className="font-body text-xs text-champagne/80 mb-4">Spend {money(remaining)} more for complimentary standard shipping.</p>
          ) : (
            <p className="font-body text-xs text-gold mb-4">Complimentary standard shipping unlocked.</p>
          )}

          <div className="space-y-2 border-t border-white/10 pt-5 font-body text-sm">
            <div className="flex justify-between text-white/60"><span>Subtotal</span><span data-testid="summary-subtotal">{money(subtotal)}</span></div>
            <div className="flex justify-between text-white/60"><span>Shipping</span><span>{shipCost === 0 ? "Free" : money(shipCost)}</span></div>
            <div className="flex justify-between text-white/40 text-xs"><span>Tax</span><span>Calculated at checkout</span></div>
            <div className="flex justify-between text-cream text-lg pt-3 border-t border-white/10 mt-3"><span className="font-serif">Total</span><span data-testid="summary-total">{money(total)}</span></div>
          </div>

          <button onClick={startCheckout} disabled={loading} data-testid="checkout-btn"
            className="w-full mt-6 py-4 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors disabled:opacity-50">
            {loading ? "Redirecting…" : "Proceed to Checkout"}
          </button>
          <p className="font-body text-[11px] text-white/35 text-center mt-3">Secure payment via Stripe</p>
        </div>
      </div>

      <CheckoutAuthDialog open={authOpen} onClose={() => setAuthOpen(false)} onProceed={proceed} />
    </div>
  );
}
